import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCurrentStateAndParams } from "@uirouter/react";
import type dayjs from "dayjs";

import { customRangeFormat } from "@similarweb/pro-logic-components";

import { swSettings } from "app/common/services/swSettings";
import { abbrNumberFilter, cpcFilter } from "app/filters/ngFilters";
import { keywordGroupsQueryClient } from "app/services/keyword-groups";
import { TABLE_TYPES_FOR_ENDPOINT } from "app/common/keyword-research/keyword-generator/constants";
import type { IRecommendation, IRelatedKeywordsApiResponse } from "app/common/keyword-research/keyword-generator/types";
import DurationService from "app/services/DurationService";
import { DefaultFetchService, requestParams } from "app/services/fetchService";
import { allTrackers } from "app/services/track/track";

import { RECOMMENDATIONS_CONFIG_ID } from "../monitor-keywords-page/consts";
import { useMonitorKeywordsEndpoints } from "../monitorKeywordsEndpoints";

const getDurationForRecommendations = () => {
    const { startDate, windowEndDate } = swSettings.components.KeywordsGeneratorV2 as {
        startDate: dayjs.Dayjs;
        windowEndDate: dayjs.Dayjs;
    };
    const startDateString = `${startDate.format(customRangeFormat)}`;
    const endDateString = `${windowEndDate.format(customRangeFormat)}`;
    return DurationService.getDurationData(`${startDateString}-${endDateString}`, "", RECOMMENDATIONS_CONFIG_ID).forAPI;
};

export const useRecommendationsQuery = (keywordGroupId: string, isUsersGroup: boolean) => {
    const queryClient = useQueryClient();
    const { country, webSource } = useCurrentStateAndParams().params;
    const endpoints = useMonitorKeywordsEndpoints();
    const { from, to, isWindow } = getDurationForRecommendations();

    const { data, isLoading, isError, isFetching, refetch } = useQuery({
        enabled: isUsersGroup,
        queryKey: ["recommendations", keywordGroupId, isUsersGroup],
        queryFn: async (): Promise<IRecommendation[]> => {
            const { GroupHash, Keywords, Name } = await keywordGroupsQueryClient.getGroupById(keywordGroupId);
            const params = {
                keyword: keywordGroupId,
                GroupHash,
                country,
                from,
                to,
                isWindow,
                websource: webSource,
                sort: "score",
                asc: false,
                rowsPerPage: 100,
                type: TABLE_TYPES_FOR_ENDPOINT.related,
            };
            const fullEndpoint = `${endpoints.recommendations}?${requestParams(params)}`;

            const response = await DefaultFetchService.getInstance().post<IRelatedKeywordsApiResponse>(fullEndpoint);
            const recommendations = response.records
                .filter((recommendation) => !Keywords.includes(recommendation.keyword))
                .map(({ keyword, cpc, averageVolume }) => ({
                    keyword: keyword,
                    cpc: cpcFilter()(cpc),
                    volume: averageVolume === 0 ? "-" : (abbrNumberFilter()(averageVolume) as string),
                }));
            allTrackers.dispatchTrackEvent(
                "Table",
                "table-results",
                `keyword suggestions presented/${Name}/${recommendations.length}`,
            );
            return recommendations;
        },
    });

    const updateRecommendations = (keywordToRemove?: string) => {
        queryClient.setQueryData(["recommendations", keywordGroupId, isUsersGroup], (oldData: IRecommendation[]) => {
            if (!oldData) {
                return;
            }
            if (keywordToRemove) {
                return oldData.filter((recommendation) => recommendation.keyword !== keywordToRemove);
            }
            return [];
        });
    };

    return {
        recommendations: data,
        isRecommendationsLoading: isLoading,
        isRecommendationsError: isError,
        isRefetching: isFetching,
        refetchRecommendations: refetch,
        updateRecommendations,
    };
};
